import React, { useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Trash2, ShoppingCart } from 'lucide-react';
import { SportsBetCartContext } from '../../_context/SportsBetCartContext';
import { SportsContext } from '../../_context/SportsContext';
import SportsnewContext from '../../_context/SportsnewContext';
import { useWalletSummary } from '../../_context/wallet.context';
import { formatOdds, toDisplayOddsDecimal } from './_helpers/oddsFormat';
import useServiceContextHook from '../../_hooks/service.context.hook';
import { notify } from '../../_helpers/notify';
import { getMarketTitle } from './figma/marketTitleResolver';
import { getSelectionLabel } from './figma/selectionLabelResolver';
import { computeBetCartMaxStake } from './_helpers/betCartStakeLimits';
import { waitForPendingBetResolution } from './_helpers/inplayDelayFlow';
import { extractBonusRecoveryGate } from '../../lib/bonusRecovery';
import { shouldResyncAfterBetError } from './_helpers/betErrorResync';
import { getSportsnewBetErrorMessage } from './_helpers/sportsnewBetErrorMessage';
import {
  clearPendingInplayBet,
  persistPendingInplayBet,
  readPendingInplayBet,
} from './_helpers/sportsPageState';
import useSportsAccountBalance from './_hooks/useSportsAccountBalance';
import useSportsBonusModalPhase from './_hooks/useSportsBonusModalPhase';
import { createSportsnewSubmissionGate } from './_helpers/sportsnewSubmissionGate';
import useBetQuotePreview from './_hooks/useBetQuotePreview';
import BonusRecoveryModal from '../../components/CommonModal/BonusRecoveryModal';
import SportsBetSuccessModal from '../../components/CommonModal/SportsBetSuccessModal';
import './sports.css';

const TYPE_LABELS = {
  DOMESTIC: '국내형',
  INTERNATIONAL: '해외형',
  SPECIAL: 'E-스포츠',
  LIVE: '라이브',
};

const DISPLAY_SCOPE_BY_CATEGORY = {
  DOMESTIC: 'domestic',
  INTERNATIONAL: 'overseas',
  SPECIAL: 'esports',
  LIVE: 'live',
};
const extractWalletPayload = (response) => {
  const payload = response?.data && typeof response.data === 'object' ? response.data : response;
  const walletSummary = payload?.walletSummary || payload?.wallet_summary;
  const balanceAfter = payload?.balanceAfter ?? payload?.balance_after;
  if (walletSummary) return walletSummary;
  if (balanceAfter != null) return { available_balance: balanceAfter };
  return null;
};

/** 베팅 카트 — 타입별 분리 */
const BETTING_TYPE_TO_BUCKET = { DOMESTIC: 'domestic', INTERNATIONAL: 'international', SPECIAL: 'special', LIVE: 'live' };

const BetCart = () => {
  const navigate = useNavigate();
  const {
    selections, stake, setStake, removeSelection, clearCart, replaceSelectionsForType,
    totalOdds, currentType, setCurrentType, otherTypeCounts,
    updateOddsInCart, oddsChanges, setBonusMultiplier,
    accountIdentity, captureAccountIdentity, isAccountIdentityCurrent
  } = useContext(SportsBetCartContext);
  const { bettingType, displaySettings, forceResyncOdds } = useContext(SportsContext);
  const { displayScope } = useContext(SportsnewContext) || {};
  const { applyWalletSummary } = useWalletSummary();
  const _fmt = displaySettings?.default_odds_format || 'decimal';
  const { session, services: { SportsService } } = useServiceContextHook();
  const sessionUserId = session?.user?.id;
  const sessionToken = session?.user?.token;
  const activeCategory = currentType || bettingType || 'DOMESTIC';
  const activeDisplayScope = displayScope || DISPLAY_SCOPE_BY_CATEGORY[activeCategory] || 'domestic';
  const isMountedRef = useRef(true);
  const captureAccountGuard = captureAccountIdentity;
  const isAccountGuardCurrent = useCallback(guard => (
    isMountedRef.current && isAccountIdentityCurrent(guard)
  ), [isAccountIdentityCurrent]);
  const capturePlacementAuthSessionCarrier = useCallback(accountGuard => (
    SportsService.capturePlacementAuthSessionCarrier({
      accountGuard, isCurrent: () => isAccountGuardCurrent(accountGuard),
    })
  ), [SportsService, isAccountGuardCurrent]);
  const {
    availableBalance, balanceLoading, refreshAvailableBalance, applyAccountWallet,
  } = useSportsAccountBalance({
    currentAccountKey: accountIdentity.accountKey,
    captureAccountGuard, isAccountGuardCurrent, applyWalletSummary,
  });
  const { open: openBonusPhase, beginConfirm, hide: hideBonusPhase, complete: completeBonusPhase, reset: resetBonusPhase } = useSportsBonusModalPhase();
  const submissionGateRef = useRef(null);
  if (!submissionGateRef.current) submissionGateRef.current = createSportsnewSubmissionGate();

  /** combo-bonus API 로드 */
  const [bonusTiers, setBonusTiers] = useState([]);
  const [bonusBuckets, setBonusBuckets] = useState({});
  const [bettingLimits, setBettingLimits] = useState(null);
  const [bettingLimitsLoading, setBettingLimitsLoading] = useState(false);
  useEffect(() => {
    const guard = captureAccountGuard();
    SportsService.getComboBonus().then(res => {
      if (!isAccountGuardCurrent(guard)) return;
      if (res?.data) setBonusTiers(res.data);
      if (res?.buckets) setBonusBuckets(res.buckets);
    }).catch(() => {});
  }, [SportsService, accountIdentity.accountKey, captureAccountGuard, isAccountGuardCurrent]);

  /** bettingType 기준 effective bonus tiers */
  const effectiveBonusTiers = useMemo(() => {
    const bucket = BETTING_TYPE_TO_BUCKET[bettingType];
    if (bucket && bonusBuckets[bucket]?.length > 0) return bonusBuckets[bucket];
    return bonusTiers;
  }, [bettingType, bonusTiers, bonusBuckets]);

  /** 다폴더 보너스 */
  const betType = selections.length <= 1 ? 'SINGLE' : 'COMBO';
  const activeBonusTier = useMemo(() => {
    if (betType !== 'COMBO' || selections.length < 3 || !effectiveBonusTiers?.length) return null;
    const applicable = effectiveBonusTiers.filter(t => selections.length >= t.fold).sort((a, b) => b.fold - a.fold);
    return applicable[0] || null;
  }, [betType, selections.length, effectiveBonusTiers]);
  const activeBonusMultiplier = activeBonusTier ? Number(activeBonusTier.value) : 1;

  /** bonusMultiplier를 context에 반영 */
  useEffect(() => {
    if (setBonusMultiplier) {
      setBonusMultiplier(activeBonusMultiplier);
    }
  }, [activeBonusMultiplier, setBonusMultiplier]);

  useEffect(() => {
    let cancelled = false;
    setBettingLimitsLoading(true);
    SportsService.getBettingLimits({
      betting_category: activeCategory,
      bet_type: betType,
    }).then(res => {
      if (!cancelled) setBettingLimits(res?.data || null);
    }).catch(() => {
      if (!cancelled) setBettingLimits(null);
    }).finally(() => {
      if (!cancelled) setBettingLimitsLoading(false);
    });
    return () => { cancelled = true; };
  }, [SportsService, activeCategory, betType, accountIdentity.accountKey]);

  useEffect(() => {
    const guard = captureAccountGuard();
    if (sessionUserId && isAccountGuardCurrent(guard)) refreshAvailableBalance(guard);
  }, [accountIdentity.accountKey, captureAccountGuard, isAccountGuardCurrent, refreshAvailableBalance, sessionUserId, sessionToken]);

  const [submitting, setSubmitting] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingBetState, setPendingBetState] = useState(null);
  const [showBonusRecoveryModal, setShowBonusRecoveryModal] = useState(false);
  const [showBetSuccessModal, setShowBetSuccessModal] = useState(false);
  const [bonusActionType, setBonusActionType] = useState('RECOVER');
  const [bonusGames, setBonusGames] = useState('sport');
  const [bonusRecoveryRequest, setBonusRecoveryRequest] = useState(null);
  const pendingBetFlowRef = useRef(null);
  const pendingBetCartHydratedIdRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  useEffect(() => {
    setSubmitting(false);
    setPendingBetState(null);
    setShowBonusRecoveryModal(false);
    setShowBetSuccessModal(false);
    setBonusRecoveryRequest(null);
    resetBonusPhase();
  }, [accountIdentity.accountKey, resetBonusPhase]);

  /** bettingType 변경 → 카트 타입 동기화 */
  useEffect(() => {
    setCurrentType(bettingType);
  }, [bettingType, setCurrentType]);

  /** 카트 배당 동기화 이벤트 */
  useEffect(() => {
    if (updateOddsInCart) {
      window.dispatchEvent(new CustomEvent('sp_cart_sync', { detail: { updateOddsInCart } }));
    }
  }, [updateOddsInCart]);

  const quickAmounts = [10000, 50000, 100000, 300000, 500000, 1000000];
  const {
    displayedBaseOdds,
    displayedBonusMultiplier,
    comboOddsAdjust,
    displayedFinalOdds,
    displayedPotentialWin,
    shouldShowFinalOdds,
  } = useBetQuotePreview({
    SportsService,
    selections,
    betType,
    bettingCategory: activeCategory,
    displayScope: activeDisplayScope,
    stake,
    totalOdds,
    bonusMultiplier: activeBonusMultiplier,
    bettingLimits,
    isAuthenticated: !!session?.user,
  });
  const maxStakeInfo = useMemo(() => computeBetCartMaxStake({
    balance: availableBalance,
    betType,
    bettingLimits,
    displayedFinalOdds,
  }), [availableBalance, betType, bettingLimits, displayedFinalOdds]);

  const handleMaxStake = async () => {
    if (!session?.user) {
      notify('로그인이 필요합니다', 'error');
      return;
    }
    if (bettingLimitsLoading || balanceLoading) {
      notify('최대 가능 금액 계산 중입니다. 잠시 후 다시 시도해주세요', 'error');
      return;
    }
    const accountGuard = captureAccountGuard();
    if (!isAccountGuardCurrent(accountGuard)) return;
    let nextMaxStake = maxStakeInfo.maxStake;
    let nextMinStake = maxStakeInfo.limits.global_min_bet;
    if (nextMaxStake <= 0) {
      const freshBalance = await refreshAvailableBalance(accountGuard);
      if (!isAccountGuardCurrent(accountGuard)) return;
      const refreshed = computeBetCartMaxStake({
        balance: freshBalance,
        betType,
        bettingLimits,
        displayedFinalOdds,
      });
      nextMaxStake = refreshed.maxStake;
      nextMinStake = refreshed.limits.global_min_bet;
    }
    if (nextMaxStake <= 0) {
      notify('보유금이 없거나 잔액 정보를 아직 불러오지 못했습니다', 'error');
      return;
    }
    if (nextMaxStake < nextMinStake) {
      setStake('');
      notify(`현재 최대 가능 금액이 최소 베팅액 ${fmt(nextMinStake)}원보다 작습니다`, 'error');
      return;
    }
    setStake(String(nextMaxStake));
  };

  const handleQuickAmount = (val) => {
    if (val === 'max') {
      handleMaxStake();
    } else {
      setStake(prev => String((Number(prev) || 0) + val));
    }
  };

  const runPendingBetFlow = useCallback(async (
    pendingBet,
    accountGuard = captureAccountGuard(),
    { restoreCartFromSnapshot = false } = {},
  ) => {
    if (!pendingBet?.betId || !isAccountGuardCurrent(accountGuard)) return;
    const currentFlow = pendingBetFlowRef.current;
    if (
      currentFlow?.betId === pendingBet.betId
      && currentFlow?.accountGuard?.userId === accountGuard.userId
      && currentFlow?.accountGuard?.generation === accountGuard.generation
    ) return;

    const flowDescriptor = Object.freeze({ pendingBet, betId: pendingBet.betId, accountGuard });
    pendingBetFlowRef.current = flowDescriptor;
    const pendingBetKey = `${accountGuard.accountKey}:${String(pendingBet.betId)}`;
    const shouldRestoreCartFromSnapshot = restoreCartFromSnapshot
      && pendingBetCartHydratedIdRef.current !== pendingBetKey;
    // A persisted snapshot may hydrate a fresh mount once. The same bet ID
    // must never hydrate again after a timeout, route re-entry, or retry,
    // because the current cart may already contain newer WS lifecycle state.
    pendingBetCartHydratedIdRef.current = pendingBetKey;

    if (pendingBet.bettingCategory === 'LIVE') {
      setCurrentType('LIVE');
    }

    if (
      shouldRestoreCartFromSnapshot
      && Array.isArray(pendingBet.selections)
      && pendingBet.selections.length > 0
    ) {
      replaceSelectionsForType?.('LIVE', pendingBet.selections);
    }

    if (pendingBet.stake !== '' && pendingBet.stake != null) {
      setStake(String(pendingBet.stake));
    }

    setPendingBetState({
      betId: pendingBet.betId,
      secondsLeft: pendingBet.secondsLeft,
      accountGuard,
    });

    try {
      await refreshAvailableBalance(accountGuard);
      if (!isAccountGuardCurrent(accountGuard)) return;
      const resolvedBet = await waitForPendingBetResolution({
        SportsService: SportsService,
        betId: pendingBet.betId,
        delaySeconds: pendingBet.secondsLeft,
        isCancelled: () => !isAccountGuardCurrent(accountGuard),
        onTick: (secondsLeft) => {
          if (!isAccountGuardCurrent(accountGuard)) return;
          setPendingBetState((prev) => (
            prev?.accountGuard === accountGuard ? { ...prev, secondsLeft } : prev
          ));
        },
      });

      if (!isAccountGuardCurrent(accountGuard)) return;

      setPendingBetState(null);
      await refreshAvailableBalance(accountGuard);

      if (!isAccountGuardCurrent(accountGuard)) return;

      if (!resolvedBet) {
        notify('인플레이 베팅 결과 확인이 지연되고 있습니다. 내 베팅에서 상태를 확인해주세요.', 'error');
        return;
      }

      clearPendingInplayBet({ userId: accountGuard.userId, betId: pendingBet.betId, backendKind: 'legacy' });

      if (resolvedBet.status === 'CONFIRMED') {
        replaceSelectionsForType?.('LIVE', []);
        setStake('');
        setMobileOpen(false);
        setShowBetSuccessModal(true);
        return;
      }

      if (Array.isArray(pendingBet.selections) && pendingBet.selections.length > 0) {
        setCurrentType('LIVE');
        if (pendingBet.stake !== '' && pendingBet.stake != null) {
          setStake(String(pendingBet.stake));
        }
        await forceResyncOdds?.(pendingBet.selections, { isCancelled: () => !isAccountGuardCurrent(accountGuard) });
        if (!isAccountGuardCurrent(accountGuard)) return;
      }

      notify(
        resolvedBet?.void_reason || '배당 변동 또는 마켓 상태 변경으로 인플레이 베팅이 거부되었습니다',
        'error'
      );
    } catch {
      if (!isAccountGuardCurrent(accountGuard)) return;
      setPendingBetState(null);
      notify('인플레이 베팅 상태 확인 중 오류가 발생했습니다. 내 베팅에서 상태를 확인해주세요.', 'error');
    } finally {
      if (pendingBetFlowRef.current === flowDescriptor) {
        pendingBetFlowRef.current = null;
      }
    }
  }, [
    SportsService,
    captureAccountGuard,
    isAccountGuardCurrent,
    forceResyncOdds,
    refreshAvailableBalance,
    replaceSelectionsForType,
    setCurrentType,
    setStake,
  ]);

  useEffect(() => {
    if (activeCategory !== 'LIVE') return;

    const pendingBet = readPendingInplayBet({ userId: accountIdentity.userId, backendKind: 'legacy' });
    if (!pendingBet) return;

    runPendingBetFlow(pendingBet, captureAccountGuard(), { restoreCartFromSnapshot: true });
  }, [activeCategory, captureAccountGuard, accountIdentity.userId, accountIdentity.accountKey, runPendingBetFlow]);

  const closeBonusRecoveryModal = useCallback(() => {
    const action = hideBonusPhase(bonusRecoveryRequest);
    if (action === 'stale') return;
    setShowBonusRecoveryModal(false);
    if (action === 'cancel') setBonusRecoveryRequest(null);
  }, [bonusRecoveryRequest, hideBonusPhase]);

  const closeBetSuccessModal = useCallback(() => {
    setShowBetSuccessModal(false);
  }, []);

  const submitBetRequest = useCallback(async ({ payload, submittedSelections, accountGuard, authSessionCarrier }) => {
    if (!isAccountGuardCurrent(accountGuard)) return;
    const requestStake = Number(payload?.stake ?? stake);
    const requestCategory = payload.betting_category;
    const res = requestCategory === 'LIVE'
      ? await SportsService.placeInplayBet(payload, { authSessionCarrier })
      : await SportsService.placeBet(payload, { authSessionCarrier });

    if (!isAccountGuardCurrent(accountGuard)) return;

    const bonusGate = extractBonusRecoveryGate(res);
    if (bonusGate) {
      setBonusGames(bonusGate.bonusGames);
      setBonusActionType(bonusGate.actionType);
      setBonusRecoveryRequest(openBonusPhase({ payload, submittedSelections, accountGuard, authSessionCarrier, actionType: bonusGate.actionType }));
      setShowBonusRecoveryModal(true);
      return;
    }

    if (res?.status) {
      const walletPayload = extractWalletPayload(res);
      if (walletPayload) {
        applyAccountWallet(walletPayload, accountGuard);
      } else {
        await refreshAvailableBalance(accountGuard);
      }
      // The native walletless success read is asynchronous and may cross accounts.
      if (!isAccountGuardCurrent(accountGuard)) return;
      const betResponse = res?.data || {};
      if (requestCategory === 'LIVE' && betResponse?.status === 'PENDING' && betResponse?.bet_id) {
        persistPendingInplayBet({
          betId: betResponse.bet_id,
          delaySeconds: betResponse.delay_seconds,
          delayExpiresAt: betResponse.delay_expires_at,
          selections: submittedSelections,
          stake: requestStake,
          bettingCategory: requestCategory,
          userId: accountGuard.userId,
          backendKind: 'legacy',
        });
        await runPendingBetFlow(
          readPendingInplayBet({ userId: accountGuard.userId, backendKind: 'legacy' }) || {
            betId: betResponse.bet_id,
            bettingCategory: requestCategory,
          userId: accountGuard.userId,
          backendKind: 'legacy',
            selections: submittedSelections,
            stake: String(requestStake),
            secondsLeft: Math.max(Math.ceil(Number(betResponse.delay_seconds) || 0), 0),
          }, accountGuard, { restoreCartFromSnapshot: false }
        );
        return;
      }

      setShowBetSuccessModal(true);
      clearCart();
      setMobileOpen(false);
      return;
    }

    const failMsg = getSportsnewBetErrorMessage(res, '베팅 실패');
    notify(failMsg, 'error');
    if (shouldResyncAfterBetError(failMsg)) {
      await forceResyncOdds?.(submittedSelections, { isCancelled: () => !isAccountGuardCurrent(accountGuard) });
    }
  }, [
    SportsService,
    applyAccountWallet,
    isAccountGuardCurrent,
    openBonusPhase,
    clearCart,
    forceResyncOdds,
    refreshAvailableBalance,
    runPendingBetFlow,
    stake,
  ]);

  const handleBonusRecoveryConfirm = useCallback(async () => {
    const request = bonusRecoveryRequest;
    if (!request || String(request.actionType).toUpperCase() === 'BLOCK'
      || !isAccountGuardCurrent(request.accountGuard) || !beginConfirm(request)) return;
    if (!submissionGateRef.current.tryAcquire(request.accountGuard)) return;
    setShowBonusRecoveryModal(false);
    setSubmitting(true);
    try {
      await submitBetRequest({
        ...request,
        payload: { ...request.payload, is_bonus_recover: true },
      });
    } catch (error) {
      if (!isAccountGuardCurrent(request.accountGuard)) return;
      const errMsg = getSportsnewBetErrorMessage(error, '베팅 오류가 발생했습니다');
      notify(errMsg, 'error');
      if (shouldResyncAfterBetError(errMsg)) await forceResyncOdds?.(request.submittedSelections, { isCancelled: () => !isAccountGuardCurrent(request.accountGuard) });
    } finally {
      const released = submissionGateRef.current.release(request.accountGuard);
      if (released && isAccountGuardCurrent(request.accountGuard)) {
        if (completeBonusPhase(request)) setBonusRecoveryRequest(null);
        setSubmitting(false);
      }
    }
  }, [beginConfirm, bonusRecoveryRequest, completeBonusPhase, forceResyncOdds, isAccountGuardCurrent, submitBetRequest]);

  const handlePlaceBet = async () => {
    const accountGuard = captureAccountGuard();
    if (!isAccountGuardCurrent(accountGuard) || submissionGateRef.current.isLocked(accountGuard)) return;
    if (!session?.user) { notify('로그인이 필요합니다', 'error'); return; }
    if (selections.length === 0) { notify('선택지를 추가하세요', 'error'); return; }
    if (!stake || Number(stake) <= 0) { notify('베팅 금액을 입력하세요', 'error'); return; }
    if (!bettingLimitsLoading && Number(stake) < maxStakeInfo.limits.global_min_bet) {
      notify(`최소 베팅액은 ${fmt(maxStakeInfo.limits.global_min_bet)}원입니다`, 'error');
      return;
    }
    const authSessionCarrier = capturePlacementAuthSessionCarrier(accountGuard);
    if (!submissionGateRef.current.tryAcquire(accountGuard)) return;
    setSubmitting(true);
    try {
      const submittedSelections = selections.map((selection) => ({ ...selection }));
      const payload = {
        bet_type: betType,
        stake: Number(stake),
        betting_category: activeCategory,
        displayScope: activeDisplayScope,
        scope: activeDisplayScope,
        selections: selections.map(s => ({
          match_id: s.match_id,
          market_id: s.market_id,
          odds_id: s.odds_id,
          ...(s.inplay_odd_id ? { inplay_odd_id: s.inplay_odd_id } : {}),
          requested_odds: toDisplayOddsDecimal(s.odds) ?? Number(s.odds),
        })),
      };
      await submitBetRequest({ payload, submittedSelections, accountGuard, authSessionCarrier });
    } catch (e) {
      if (!isAccountGuardCurrent(accountGuard)) return;
      const errMsg = getSportsnewBetErrorMessage(e, '베팅 오류가 발생했습니다');
      notify(errMsg, 'error');
      /** 배당 변경 에러 시 목록/카트 즉시 재동기화 */
      if (shouldResyncAfterBetError(errMsg)) {
        await forceResyncOdds?.(selections, { isCancelled: () => !isAccountGuardCurrent(accountGuard) });
      }
    } finally {
      const released = submissionGateRef.current.release(accountGuard);
      if (released && isAccountGuardCurrent(accountGuard)) {
        setSubmitting(false);
      }
    }
  };

  const fmt = (n) => Number(n).toLocaleString('ko-KR');
  const comboAdjustText = comboOddsAdjust === 0
    ? '0.00'
    : `${comboOddsAdjust > 0 ? '+' : '-'}${Math.abs(comboOddsAdjust).toFixed(2)}`;

  const typeBadges = Object.entries(otherTypeCounts).filter(([t]) => t !== currentType);

  const cartContent = (
    <div className="sp_cart">
      {/* ── Figma: BETCART / MYBET 탭 (각 150px, 48px 높이) ── */}
      <div className="sp_cart_header">
        <button type="button" className="sp_cart_tab sp_cart_tab_active">
          <ShoppingCart size={14} /> BETCART
          {selections.length > 0 && <span className="sp_cart_count_badge">{selections.length}</span>}
        </button>
        <button type="button" className="sp_cart_tab" onClick={() => navigate('/betting-history?tab=Sportsbook')}>
          내 베팅
        </button>
      </div>

      {/* ── Figma: 합계 N + toggle/clear ── */}
      <div className="sp_cart_summary_row">
        <span>합계 <strong>{selections.length}</strong></span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {typeBadges.length > 0 && typeBadges.map(([t, cnt]) => (
            <button
              key={t}
              type="button"
              className="sp_cart_type_badge"
              onClick={() => setCurrentType(t)}
              title={`${TYPE_LABELS[t] || t} (${cnt})`}
            >
              {TYPE_LABELS[t] || t} {cnt}
            </button>
          ))}
          {selections.length > 0 && (
            <button type="button" className="sp_cart_item_remove" onClick={clearCart} title="전체삭제">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── Figma: 카드형 선택 항목 목록 ── */}
      <div className={`sp_cart_items${selections.length >= 3 ? ' sp_cart_items_scroll' : ''}`}>
        {selections.length === 0 && (
          <div className="sp_cart_empty">
            <ShoppingCart size={36} className="sp_cart_empty_icon" />
            <p className="sp_cart_empty_title">베팅 카트가 비어있습니다</p>
            <p className="sp_cart_empty_desc">배당을 클릭하여 추가하세요</p>
          </div>
        )}
        {selections.map(sel => (
          (() => {
            const marketTitle = sel.market_title_display_ko
              || sel.market_name_ko
              || ((sel.market_title_display && sel.market_title_display !== '마켓') ? sel.market_title_display : null)
              || sel.market_name
              || getMarketTitle(sel.market_name);
            return (
          <div key={sel.odds_id} className="sp_cart_item">
            {/* top: flag+league + X close */}
            <div className="sp_cart_item_top">
              <span className="sp_cart_item_match">{sel.match_name}</span>
              <button type="button" className="sp_cart_item_remove" onClick={() => removeSelection(sel.odds_id)}>
                <X size={12} />
              </button>
            </div>
            {/* market name */}
            <div className="sp_cart_item_market">
              {marketTitle}
              {sel.market_line ? ` (${String(sel.market_line).replace(/\s*\(0-0\)\s*$/, '')})` : ''}
            </div>
            {/* selection + odds (pink) */}
            <div className="sp_cart_item_sel">
              <span className="sp_cart_item_name">{
                sel.selection_name_display
                  || getSelectionLabel(
                    { name: sel.selection_name },
                    { marketName: marketTitle, match: sel }
                  )
              }</span>
              <span className={`sp_cart_item_odds${oddsChanges?.[sel.odds_id] === 'up' ? ' sp_cart_odds_up' : oddsChanges?.[sel.odds_id] === 'down' ? ' sp_cart_odds_down' : ''}`}>
                {formatOdds(sel.odds, _fmt)}
              </span>
            </div>
          </div>
            );
          })()
        ))}
      </div>

      {/* ── Figma: Footer section ── */}
      <div className="sp_cart_footer">
        {/* 캐쉬 + 베팅 확률 */}
        <div className="sp_cart_summary_row">
          <span>캐쉬</span>
          <span>{balanceLoading && availableBalance <= 0 ? '조회중' : fmt(availableBalance || 0)}</span>
        </div>
        {betType === 'COMBO' && (
          <div className="sp_cart_summary_row">
            <span>베팅 확률</span>
            <span style={{ color: 'var(--sb-primary)', fontWeight: 700 }}>{formatOdds(displayedBaseOdds, _fmt)}x</span>
          </div>
        )}
        {betType === 'COMBO' && activeBonusTier && (
          <div className="sp_cart_summary_row">
            <span>다폴더 보너스 ({activeBonusTier.fold}폴)</span>
            <span style={{ color: '#4CAF50', fontWeight: 700 }}>x{Number(displayedBonusMultiplier).toFixed(2)}</span>
          </div>
        )}
        {betType === 'COMBO' && Math.abs(comboOddsAdjust) > 0.001 && (
          <div className="sp_cart_summary_row">
            <span>조합배당조정</span>
            <span style={{ color: comboOddsAdjust > 0 ? 'var(--sb-primary)' : '#ef4444', fontWeight: 700 }}>
              {comboAdjustText}
            </span>
          </div>
        )}
        {shouldShowFinalOdds && (
          <div className="sp_cart_summary_row">
            <span>최종배당</span>
            <span style={{ color: 'var(--sb-primary)', fontWeight: 800 }}>{formatOdds(displayedFinalOdds, _fmt)}x</span>
          </div>
        )}

        {/* 당첨예상금액 */}
        <div className="sp_cart_summary_row">
          <span>당첨예상금액</span>
          <span style={{ fontWeight: 700 }}>{fmt(Math.floor(displayedPotentialWin))}</span>
        </div>

        {/* 베팅 금액 input */}
        <div className="sp_cart_summary_row">
          <span>베팅 금액</span>
          <input
            type="number"
            className="sp_cart_stake_input"
            placeholder="0"
            value={stake}
            onChange={e => setStake(e.target.value)}
            style={{ color: 'var(--sb-primary)' }}
          />
        </div>

        {/* 3x2 금액 버튼 그리드 */}
        <div className="sp_cart_amounts">
          {quickAmounts.map(v => (
            <button key={v} type="button" className="sp_cart_amt_btn" onClick={() => handleQuickAmount(v)}>
              {fmt(v)}
            </button>
          ))}
        </div>

        {/* 3-button 액션 row: 리셋 | 최대 금액 | 최소 금액 */}
        <div className="sp_cart_actions">
          <button type="button" className="sp_cart_amt_btn" onClick={() => setStake('')}>리셋</button>
          <button type="button" className="sp_cart_amt_btn" onClick={() => handleQuickAmount('max')}>최대 금액</button>
          <button type="button" className="sp_cart_amt_btn" onClick={() => setStake(String(maxStakeInfo.limits.global_min_bet))}>최소 금액</button>
        </div>
        {selections.length > 0 && (
          <div style={{ marginTop: '8px', fontSize: '11px', color: '#6b7280' }}>
            {bettingLimitsLoading || balanceLoading
              ? '최대 가능 금액 계산 중...'
              : maxStakeInfo.canBetAtLeastMin
              ? `최대 가능 금액: ${fmt(maxStakeInfo.maxStake)}원 (보유금/베팅한도/당첨한도 기준)`
              : `현재 한도로는 최소 베팅액 ${fmt(maxStakeInfo.limits.global_min_bet)}원 이상 베팅할 수 없습니다`}
          </div>
        )}

        {/* Full-width Betting CTA */}
        <button
          type="button"
          className="sp_cart_submit"
          onClick={handlePlaceBet}
          disabled={submitting || selections.length === 0 || !(Number(stake) > 0)}
        >
          {submitting ? '처리중...' : 'Betting'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop: always visible right panel */}
      <div className="sp_cart_desktop">{cartContent}</div>

      {/* Mobile: floating bar + slide-up overlay */}
      <div className="sp_cart_mobile_bar" onClick={() => selections.length > 0 && setMobileOpen(true)}>
        <ShoppingCart size={18} />
        <span className="sp_cart_mobile_label">베팅카트 ({selections.length})</span>
        {selections.length > 0 && (
          <span className="sp_cart_mobile_win">{'\u20A9'}{fmt(Math.floor(displayedPotentialWin))}</span>
        )}
      </div>
      {mobileOpen && (
        <div className="sp_cart_overlay" onClick={(e) => { if (e.target === e.currentTarget) setMobileOpen(false); }}>
          <div className="sp_cart_slide">
            <div className="sp_cart_slide_close">
              <button type="button" onClick={() => setMobileOpen(false)}><X size={20} /> 닫기</button>
            </div>
            {cartContent}
          </div>
        </div>
      )}
      {pendingBetState && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          background: 'rgba(3, 14, 19, 0.82)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '360px',
            borderRadius: '18px',
            background: '#142d37',
            border: '1px solid rgba(166, 196, 213, 0.18)',
            boxShadow: '0 24px 60px rgba(0, 0, 0, 0.48)',
            padding: '24px 22px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: '#f3f8fa' }}>
              인플레이 베팅 확인 중
            </div>
            <div style={{
              marginTop: '14px',
              fontSize: '34px',
              fontWeight: 900,
              color: 'var(--sb-primary)',
              lineHeight: 1,
            }}>
              {pendingBetState.secondsLeft > 0 ? pendingBetState.secondsLeft : '...'}
            </div>
            <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 700, color: '#e7f0f4' }}>
              배당변동 확인중
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.5, color: '#a6c4d5' }}>
              {pendingBetState.secondsLeft > 0
                ? `${pendingBetState.secondsLeft}초 동안 배당과 마켓 상태를 다시 확인합니다.`
                : '최종 베팅 결과를 확인하고 있습니다.'}
            </div>
            <div style={{ marginTop: '12px', fontSize: '12px', lineHeight: 1.5, color: '#7894a0' }}>
              배당이 변동되거나 마켓이 닫히면 베팅이 거부될 수 있습니다.
            </div>
          </div>
        </div>
      )}

      <BonusRecoveryModal
        show={showBonusRecoveryModal}
        onHide={closeBonusRecoveryModal}
        onConfirm={handleBonusRecoveryConfirm}
        onCancel={closeBonusRecoveryModal}
        gameType="sport"
        bonusgames={bonusGames}
        actionType={bonusActionType}
      />
      <SportsBetSuccessModal
        show={showBetSuccessModal}
        onClose={closeBetSuccessModal}
      />
    </>
  );
};

export default BetCart;
